import React, { useState, useMemo, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { evaluateBp } from "../fields/bpPlausibility";

interface InlineBPCaptureNodeProps {
  isDarkMode?: boolean;
  patientAgeYears?: number;
  onSave: (vitalPayload: any) => void;
  onAnswer: (answer: { conclusion: "optionA" | "optionB" }) => void;
  stepNumber?: number;
  existingSystolic?: number | null;
  existingDiastolic?: number | null;
  /** Opt-in: range checks, "mmHg" hints, text inputs with a numeric keypad, and errors on leaving a field. */
  plausibilityChecks?: boolean;
  /** The reading already saved for this step; fills the fields when the step is revisited. */
  initialCapture?: { data?: { systolic?: string; diastolic?: string } } | null;
}

export function InlineBPCaptureNode({
  isDarkMode = false,
  patientAgeYears,
  onSave,
  onAnswer,
  stepNumber,
  existingSystolic,
  existingDiastolic,
  plausibilityChecks = false,
  initialCapture,
}: InlineBPCaptureNodeProps) {
  const [systolic, setSystolic] = useState(initialCapture?.data?.systolic ?? "");
  const [diastolic, setDiastolic] = useState(initialCapture?.data?.diastolic ?? "");
  const [shown, setShown] = useState({ systolic: false, diastolic: false });
  const hide = (f: "systolic" | "diastolic") => setShown((s) => (s[f] ? { ...s, [f]: false } : s));
  const reveal = (f: "systolic" | "diastolic") => setShown((s) => (s[f] ? s : { ...s, [f]: true }));

  const revisited = !!(initialCapture?.data?.systolic || initialCapture?.data?.diastolic);

  useEffect(() => {
    // A reading already saved at this step wins over the patient-record prefill.
    if (revisited) return;
    if (existingSystolic && existingSystolic > 0) {
      setSystolic(String(existingSystolic));
    } else {
      setSystolic("");
    }
    if (existingDiastolic && existingDiastolic > 0) {
      setDiastolic(String(existingDiastolic));
    } else {
      setDiastolic("");
    }
  }, [existingSystolic, existingDiastolic, revisited]);

  const systolicThreshold = (patientAgeYears ?? 0) >= 75 ? 150 : 140;
  const diastolicThreshold = 90;

  const checked = useMemo(
    () => (plausibilityChecks ? evaluateBp(systolic, diastolic) : null),
    [plausibilityChecks, systolic, diastolic],
  );

  const sys = checked ? (checked.systolic.value ?? NaN) : parseInt(systolic, 10);
  const dia = checked ? (checked.diastolic.value ?? NaN) : parseInt(diastolic, 10);
  const isValid = checked ? checked.blockedReason === "" : !isNaN(sys) && sys > 0 && !isNaN(dia) && dia > 0;

  const controlled = useMemo(
    () => isValid && sys < systolicThreshold && dia < diastolicThreshold,
    [isValid, sys, dia, systolicThreshold, diastolicThreshold],
  );

  const handleContinue = () => {
    if (!isValid) {
      if (checked) setShown({ systolic: true, diastolic: true });
      return;
    }
    onSave({
      type: "blood-pressure",
      data: {
        systolic: systolic,
        diastolic: diastolic,
        unit: "mmHg",
      },
      timestamp: new Date(),
    });
    onAnswer({ conclusion: controlled ? "optionA" : "optionB" });
  };

  const card: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
    borderRadius: "1rem",
    padding: "1rem",
    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: isDarkMode ? "#d1d5db" : "#374151",
    display: "block",
    marginBottom: "0.5rem",
  };

  const subLabelStyle: React.CSSProperties = {
    fontSize: "0.75rem",
    color: isDarkMode ? "#9ca3af" : "#6b7280",
  };

  const fieldError = (id: string, message?: string) =>
    message ? (
      <p
        id={id}
        data-testid={id}
        style={{ display: "flex", alignItems: "flex-start", gap: "0.375rem", fontSize: "0.875rem", color: "#b91c1c", margin: "0.25rem 0 0" }}
      >
        <AlertCircle style={{ marginTop: "0.125rem", height: "1rem", width: "1rem", flexShrink: 0 }} aria-hidden="true" />
        <span>{message}</span>
      </p>
    ) : null;

  const checkedInput = (field: "systolic" | "diastolic", label: string, value: string, set: (v: string) => void) => {
    const reading = checked![field];
    const invalid = reading.status === "invalid" && shown[field];
    return (
      <div style={{ flex: 1 }}>
        <Label htmlFor={`bp-${field}`} style={{ ...subLabelStyle, display: "block", marginBottom: "0.125rem" }}>
          {label}
        </Label>
        <p id={`bp-${field}-hint`} style={{ ...subLabelStyle, margin: "0 0 0.25rem" }}>
          mmHg
        </p>
        <Input
          id={`bp-${field}`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={`${label} blood pressure`}
          aria-invalid={invalid || undefined}
          aria-describedby={`bp-${field}-hint${invalid ? ` bp-${field}-error` : ""}`}
          value={value}
          onChange={(e) => {
            hide(field);
            set(e.target.value);
          }}
          onBlur={() => reveal(field)}
          style={invalid ? { borderColor: "#ef4444" } : undefined}
        />
        {fieldError(`bp-${field}-error`, shown[field] ? reading.error : undefined)}
      </div>
    );
  };

  const thresholdPanelStyle: React.CSSProperties = {
    marginBottom: "1rem",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.5rem",
    fontSize: "0.875rem",
    backgroundColor: isDarkMode ? "#374151" : "#f9fafb",
    color: isDarkMode ? "#d1d5db" : "#4b5563",
  };

  return (
    <div style={card}>
      {stepNumber !== undefined && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "1.5rem",
              height: "1.5rem",
              borderRadius: "9999px",
              fontSize: "0.875rem",
              fontWeight: 600,
              backgroundColor: isDarkMode ? "#ffffff" : "#000000",
              color: isDarkMode ? "#000000" : "#ffffff",
            }}
          >
            {stepNumber}
          </div>
          <h3
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              margin: 0,
              color: isDarkMode ? "#ffffff" : "#111827",
            }}
          >
            Record blood pressure
          </h3>
        </div>
      )}
      {stepNumber === undefined && (
        <h3
          style={{
            fontSize: "0.875rem",
            fontWeight: 600,
            marginBottom: "1rem",
            marginTop: 0,
            color: isDarkMode ? "#ffffff" : "#111827",
          }}
        >
          Record blood pressure
        </h3>
      )}

      <p
        style={{
          fontSize: "0.75rem",
          marginBottom: "1rem",
          color: isDarkMode ? "#9ca3af" : "#6b7280",
        }}
      >
        Enter the patient's blood pressure reading below. The result will be
        saved to the patient record and evaluated automatically.
      </p>

      <div style={{ marginBottom: "1rem" }}>
        <Label style={labelStyle}>Blood pressure (mmHg)</Label>
        <div style={{ display: "flex", alignItems: checked ? "flex-start" : "center", gap: "0.5rem" }}>
          {checked ? (
            <>
              {checkedInput("systolic", "Systolic", systolic, setSystolic)}
              <span style={{ fontSize: "1.25rem", fontWeight: 300, marginTop: "2.5rem", color: "#9ca3af" }}>/</span>
              {checkedInput("diastolic", "Diastolic", diastolic, setDiastolic)}
            </>
          ) : (
            <>
              <div style={{ flex: 1 }}>
                <p style={{ ...subLabelStyle, marginBottom: "0.25rem", marginTop: 0 }}>Systolic</p>
                <Input
                  type="number"
                  placeholder="120"
                  value={systolic}
                  onChange={(e) => setSystolic(e.target.value)}
                />
              </div>
              <span
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 300,
                  marginTop: "1rem",
                  color: "#9ca3af",
                }}
              >
                /
              </span>
              <div style={{ flex: 1 }}>
                <p style={{ ...subLabelStyle, marginBottom: "0.25rem", marginTop: 0 }}>Diastolic</p>
                <Input
                  type="number"
                  placeholder="80"
                  value={diastolic}
                  onChange={(e) => setDiastolic(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div style={thresholdPanelStyle}>
        <p style={{ fontWeight: 500, marginBottom: "0.125rem", marginTop: 0 }}>
          Controlled threshold for this patient: &lt;{systolicThreshold}/
          {diastolicThreshold} mmHg
        </p>
        {(patientAgeYears ?? 0) >= 75 && (
          <p
            style={{
              fontSize: "0.75rem",
              marginTop: "0.125rem",
              marginBottom: 0,
              color: isDarkMode ? "#9ca3af" : "#6b7280",
            }}
          >
            Age 75 or over — adjusted threshold applies (150/90 mmHg)
          </p>
        )}
      </div>

      {isValid &&
        (controlled ? (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              backgroundColor: isDarkMode ? "rgba(6, 78, 59, 0.3)" : "#f0fdf4",
              border: `1px solid ${isDarkMode ? "#15803d" : "#bbf7d0"}`,
              color: isDarkMode ? "#86efac" : "#166534",
            }}
          >
            BP controlled — within threshold
          </div>
        ) : (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              backgroundColor: isDarkMode ? "rgba(120, 53, 15, 0.3)" : "#fffbeb",
              border: `1px solid ${isDarkMode ? "#b45309" : "#fde68a"}`,
              color: isDarkMode ? "#fcd34d" : "#92400e",
            }}
          >
            BP above threshold — Mysimba will be excluded
          </div>
        ))}

      <FlowPrimaryButton
        label="Save and continue"
        onClick={handleContinue}
        disabled={!isValid}
        blockedReason={checked?.blockedReason || "Enter systolic and diastolic readings to continue."}
        size="lg"
        className="w-full"
      />
    </div>
  );
}
